import { produce } from 'immer';
import * as React from 'react';

import {
  CLEARED,
  ContextType,
  EDITING,
  HANDLE_BACK_NAVIGATION,
  HANDLE_SUBMIT,
  RESOLVING,
  RuleAction,
  RuleProviderProps,
  RuleState,
  SUBMITTED,
  SUBMITTING,
  TransactionRuleType,
  UPDATE_APPLY_TO_EXISTING,
  UPDATE_DUPLICATE_ID,
  UPDATE_ERROR,
  UPDATE_FROM_TXN,
  UPDATE_RULE,
  UPDATE_SERVICE_LEVEL,
  UPDATE_STAGE,
  VALIDATED,
  VALIDATING,
} from 'app-components/flux/transaction-rules/transactionRulesTypes';

export const initialState: RuleState = {
  serviceLevel: 'BASIC',
  stage: undefined,
  backText: '',
  buttonHelperText: '',
  buttonText: '',
  titleText: '',
  rule: undefined,
  onUpdate: (rule?: TransactionRuleType) => {
    rule;
  },
  duplicateId: undefined,
  error: undefined,
  fromTxn: false,
  editField: undefined,
  statementPayeeName: undefined,
  inferredPayeeName: undefined,
};

const getApplyHelperText = (applyToExisting: boolean): string =>
  applyToExisting ? 'Rule will apply to existing & future transactions' : 'Rule will apply to future transactions';

const stateChangeTextHelper = (ruleState: RuleState) => {
  const { stage, rule } = ruleState;
  const isUpdate = !!rule?.id;
  const applyFlag = !!rule?.applyToExistingTransactions;

  if (stage === EDITING || stage === VALIDATING) {
    return {
      titleText: isUpdate ? 'Update Rule' : 'Create Rule',
      backText: 'Back to Rules',
      buttonText: 'Continue to review',
      buttonHelperText: 'You’ll handle existing transactions next',
    };
  }
  if (stage === VALIDATED) {
    return {
      titleText: 'Review Rule',
      backText: 'Back to Edit',
      buttonText: isUpdate ? 'Update Rule' : 'Create Rule',
      buttonHelperText: getApplyHelperText(applyFlag),
    };
  }
  return {};
};

const updateStage = (draft: RuleState) => {
  switch (draft.stage) {
    case EDITING:
    case VALIDATING:
      Object.assign(draft, stateChangeTextHelper(draft));
      break;
    case VALIDATED:
      if (draft.error) {
        draft.stage = EDITING;
        return;
      }
      Object.assign(draft, stateChangeTextHelper(draft));
      break;
    case SUBMITTING:
      break;
    case SUBMITTED:
      draft.rule = undefined;
      draft.stage = EDITING;
      break;
    case CLEARED:
      draft.rule = undefined;
      draft.stage = undefined;
      draft.duplicateId = undefined;
      draft.error = undefined;
      break;
    default:
      break;
  }
};

const RuleStateReducer = (state: RuleState, action: RuleAction) => {
  const newState = produce(state, (draft) => {
    switch (action.type) {
      case UPDATE_DUPLICATE_ID:
        draft.duplicateId = action.payload;
        draft.stage = RESOLVING;
        break;
      case UPDATE_SERVICE_LEVEL:
        draft.serviceLevel = action.payload;
        break;
      case UPDATE_STAGE:
        draft.stage = action.payload;
        updateStage(draft);
        break;
      case UPDATE_APPLY_TO_EXISTING:
        if (draft.rule) {
          draft.rule.applyToExistingTransactions = action.payload;
          draft.buttonHelperText = getApplyHelperText(!!draft.rule?.applyToExistingTransactions);
        }
        break;
      case UPDATE_RULE:
        draft.rule = action.payload;
        break;
      case UPDATE_ERROR:
        draft.error = action.payload;
        break;
      case HANDLE_SUBMIT:
        if (action.payload === EDITING) {
          draft.stage = VALIDATING;
        } else if (action.payload === VALIDATED) {
          draft.stage = SUBMITTING;
        }
        break;
      case HANDLE_BACK_NAVIGATION:
        switch (action.payload) {
          case VALIDATED:
            draft.stage = EDITING;
            updateStage(draft);
            break;
          case EDITING:
            {
              draft.stage = undefined;
              draft.rule = undefined;
            }
            break;
          default:
            break;
        }
        break;
      case UPDATE_FROM_TXN:
        draft.fromTxn = action.payload;
        break;
      default:
        break;
    }
  });

  return newState;
};

const RuleContext = React.createContext<ContextType>({
  state: initialState,
  dispatch: () => {},
});
RuleContext.displayName = 'Rule Context';

export const RuleProvider = ({
  statementPayeeName,
  inferredPayeeName,
  rule,
  onUpdate,
  children,
  fromTxn,
  editField,
}: RuleProviderProps) => {
  const [state, dispatch] = React.useReducer(RuleStateReducer, {
    ...initialState,
    rule,
    onUpdate: onUpdate,
    fromTxn: !!fromTxn,
    statementPayeeName,
    inferredPayeeName,
    editField: editField,
  });
  const ctx = React.useMemo(() => ({ state, dispatch }), [dispatch, state]);

  return <RuleContext.Provider value={ctx}>{children}</RuleContext.Provider>;
};

export function useStateContext() {
  return React.useContext(RuleContext);
}
